import { useCallback, useState } from "react";
import { Upload, X } from "lucide-react";

const GENRES = [
    { id: "femme", label: "Femme" },
    { id: "homme", label: "Homme" },
    { id: "mixte", label: "Mixte" },
    { id: "enfant", label: "Enfant" },
];

const PRESETS = [
    { name: "Émeraude", bg: "#3D6E5B", fg: "#FAF6EF" },
    { name: "Émeraude clair", bg: "#A3CFBB", fg: "#0F2A20" },
    { name: "Terracotta", bg: "#DD7259", fg: "#FAF6EF" },
    { name: "Lavande", bg: "#ECB6FF", fg: "#0F2A20" },
    { name: "Jaune", bg: "#F2D580", fg: "#0F2A20" },
    { name: "Crème", bg: "#FAF6EF", fg: "#0F2A20" },
    { name: "Encre", bg: "#0F2A20", fg: "#FAF6EF" },
];

const EMPTY = {
    marque: "",
    modele: "",
    couleur: "",
    materiau: "",
    prix: "Sur devis",
    genre: "mixte",
    description: "",
    bg: "#3D6E5B",
    fg: "#FAF6EF",
    coupDeCoeur: false,
    coupDeCoeurNote: "",
    moment: false,
    image: null,
};

const MAX_BYTES = 1_500_000; // ~1.5 Mo (base64 ~= 2 Mo)

function readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
        reader.readAsDataURL(file);
    });
}

export default function MontureForm({ initial, onCancel, onSubmit, submitting }) {
    const [data, setData] = useState({ ...EMPTY, ...(initial || {}) });
    const [imageError, setImageError] = useState("");

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const onPickImage = useCallback(async (e) => {
        setImageError("");
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_BYTES) {
            setImageError(
                `Image trop lourde (${(file.size / 1024 / 1024).toFixed(
                    1
                )} Mo). Maximum 1,5 Mo.`
            );
            e.target.value = "";
            return;
        }
        try {
            const dataUrl = await readAsDataUrl(file);
            setData((d) => ({ ...d, image: dataUrl }));
        } catch (err) {
            setImageError(err.message);
        }
        e.target.value = "";
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        // Nettoyer le coupDeCoeurNote si pas coup de coeur
        const payload = { ...data };
        if (!payload.coupDeCoeur) payload.coupDeCoeurNote = "";
        if (payload.coupDeCoeurNote === "") payload.coupDeCoeurNote = null;
        onSubmit(payload);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-lpl-bg border border-lpl-border p-6 md:p-8"
            data-testid="monture-form"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* LEFT — image */}
                <div>
                    <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mb-2">
                        Photo
                    </span>
                    <div
                        className="aspect-[4/5] flex items-center justify-center overflow-hidden relative"
                        style={{ background: data.bg, color: data.fg }}
                    >
                        {data.image ? (
                            <>
                                <img
                                    src={data.image}
                                    alt="Aperçu"
                                    className="w-full h-full object-cover"
                                />
                                <button
                                    type="button"
                                    onClick={() => set("image", null)}
                                    aria-label="Retirer la photo"
                                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-lpl-primary text-lpl-text flex items-center justify-center hover:bg-lpl-terracotta"
                                    data-testid="monture-form-image-clear"
                                >
                                    <X size={16} />
                                </button>
                            </>
                        ) : (
                            <div className="text-center px-6">
                                <Upload size={32} strokeWidth={1.5} className="mx-auto mb-3 opacity-80" />
                                <p className="font-display text-sm opacity-80">
                                    Pas de photo. Le pictogramme s'affichera à la place.
                                </p>
                            </div>
                        )}
                    </div>

                    <label className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-lpl-primary text-lpl-text rounded-full text-xs uppercase tracking-[0.16em] font-semibold cursor-pointer hover:bg-lpl-text hover:text-lpl-bg transition-colors">
                        <Upload size={14} />
                        Choisir une image
                        <input
                            type="file"
                            accept="image/*"
                            onChange={onPickImage}
                            className="hidden"
                            data-testid="monture-form-image-input"
                        />
                    </label>
                    {imageError && (
                        <p
                            className="mt-2 text-sm text-lpl-terracotta"
                            data-testid="monture-form-image-error"
                        >
                            {imageError}
                        </p>
                    )}

                    <div className="mt-6">
                        <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mb-2">
                            Palette de fond
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {PRESETS.map((p) => {
                                const active = p.bg === data.bg;
                                return (
                                    <button
                                        key={p.name}
                                        type="button"
                                        onClick={() => {
                                            set("bg", p.bg);
                                            set("fg", p.fg);
                                        }}
                                        aria-pressed={active}
                                        className={`px-3 py-2 text-xs font-semibold border-2 transition-colors ${
                                            active
                                                ? "border-lpl-text"
                                                : "border-lpl-border hover:border-lpl-text"
                                        }`}
                                        style={{ background: p.bg, color: p.fg }}
                                        data-testid={`palette-${p.name}`}
                                    >
                                        {p.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* RIGHT — fields */}
                <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Marque" required>
                            <input
                                required
                                value={data.marque}
                                onChange={(e) => set("marque", e.target.value)}
                                className={inputCls}
                                data-testid="monture-form-marque"
                            />
                        </Field>
                        <Field label="Modèle" required>
                            <input
                                required
                                value={data.modele}
                                onChange={(e) => set("modele", e.target.value)}
                                className={inputCls}
                                data-testid="monture-form-modele"
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Couleur" required>
                            <input
                                required
                                value={data.couleur}
                                onChange={(e) => set("couleur", e.target.value)}
                                className={inputCls}
                                data-testid="monture-form-couleur"
                            />
                        </Field>
                        <Field label="Matériau" required>
                            <input
                                required
                                value={data.materiau}
                                onChange={(e) => set("materiau", e.target.value)}
                                className={inputCls}
                                data-testid="monture-form-materiau"
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Tarif">
                            <input
                                value={data.prix}
                                onChange={(e) => set("prix", e.target.value)}
                                className={inputCls}
                                data-testid="monture-form-prix"
                            />
                        </Field>
                        <Field label="Genre">
                            <select
                                value={data.genre}
                                onChange={(e) => set("genre", e.target.value)}
                                className={inputCls}
                                data-testid="monture-form-genre"
                            >
                                {GENRES.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    <Field label="Description" required>
                        <textarea
                            required
                            rows={4}
                            value={data.description}
                            onChange={(e) => set("description", e.target.value)}
                            className={`${inputCls} resize-y`}
                            data-testid="monture-form-description"
                        />
                    </Field>

                    <div className="pt-2 border-t border-lpl-border space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={data.moment}
                                onChange={(e) => set("moment", e.target.checked)}
                                className="w-5 h-5 accent-lpl-primary"
                                data-testid="monture-form-moment"
                            />
                            <span className="text-sm font-medium">
                                Mettre en avant dans <em>« Nos coups de projecteur »</em>{" "}
                                (carousel)
                            </span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={data.coupDeCoeur}
                                onChange={(e) => set("coupDeCoeur", e.target.checked)}
                                className="w-5 h-5 accent-lpl-terracotta"
                                data-testid="monture-form-coupdecoeur"
                            />
                            <span className="text-sm font-medium">
                                Coup de cœur de Sylvie (un seul à la fois)
                            </span>
                        </label>
                    </div>

                    {data.coupDeCoeur && (
                        <Field label="Note coup de cœur">
                            <textarea
                                rows={3}
                                value={data.coupDeCoeurNote || ""}
                                onChange={(e) => set("coupDeCoeurNote", e.target.value)}
                                className={`${inputCls} resize-y`}
                                placeholder="Le petit mot de Sylvie sur cette monture…"
                                data-testid="monture-form-cdc-note"
                            />
                        </Field>
                    )}
                </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 justify-end">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-6 py-3 rounded-full border-2 border-lpl-border text-lpl-text font-body text-sm font-medium hover:border-lpl-text transition-colors"
                    data-testid="monture-form-cancel"
                >
                    Annuler
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-lpl-primary text-lpl-text text-sm uppercase tracking-[0.16em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors disabled:opacity-60"
                    data-testid="monture-form-submit"
                >
                    {submitting ? "Enregistrement…" : "Enregistrer"}
                </button>
            </div>
        </form>
    );
}

const inputCls =
    "w-full px-3 py-2.5 bg-lpl-bg-alt border-2 border-lpl-border focus:border-lpl-text outline-none font-body text-sm text-lpl-text";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-lpl-text-muted mb-2">
                {label}
                {required && <span className="text-lpl-terracotta ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}
